import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Panel, Typography } from "@/components/atoms";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { clsx } from "@/utils";
import { StepIndicator } from "@/components/molecules";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { VehicleDetailsSummary } from "./common/VehicleDetailsSummary";
import { Step1VehicleInfo } from "./steps/Step1VehicleInfo";
import { Step2DriverAccess } from "./steps/Step2DriverAccess";
import { Step3VehicleDocuments } from "./steps/Step3VehicleDocuments";
import { Step4Review } from "./steps/Step4Review";
import { FinishLaterModal } from "./modals/FinishLaterModal";
import { AddVehicleWizardProvider, useAddVehicleWizardContext } from "./AddVehicleWizardContext";

const TOTAL_STEPS = 4;

const STEP_TITLES: Record<number, { title: string; description: string }> = {
  1: { title: "Complete Vehicle Information", description: "Provide the remaining details to complete this vehicle's setup." },
  2: { title: "Driver Access", description: "Search and add drivers who will have access to this vehicle." },
  3: { title: "Vehicle Documents", description: "Upload the required documents for this vehicle." },
  4: { title: "Review & Confirm", description: "Review all the details before adding this vehicle." },
};

export const AddVehicleWizard = () => {
  return (
    <AddVehicleWizardProvider>
      <WizardContent />
    </AddVehicleWizardProvider>
  );
};

const WizardContent = () => {
  const navigate = useNavigate();
  const { vehicleId, vehicle, refetchVehicle } = useAddVehicleWizardContext();
  const [currentStep, setCurrentStep] = useState(vehicle.setupStep ?? 1);
  const [showFinishLater, setShowFinishLater] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  // If vehicle setup is already complete, redirect to vehicle view
  if (vehicle.setupStep == null) {
    navigate(`../${vehicleId}`, { replace: true });
    return null;
  }

  const stepInfo = STEP_TITLES[currentStep];

  const handleFinishLater = () => {
    setShowFinishLater(true);
  };

  const handleConfirmFinishLater = () => {
    setShowFinishLater(false);
    navigate("../");
  };

  const handleNext = async () => {
    await refetchVehicle();
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1VehicleInfo vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onFinishLater={handleFinishLater} />;
      case 2:
        return <Step2DriverAccess vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onBack={handleBack} onFinishLater={handleFinishLater} />;
      case 3:
        return <Step3VehicleDocuments vehicleId={vehicleId} vehicle={vehicle} onNext={handleNext} onBack={handleBack} onFinishLater={handleFinishLater} />;
      case 4:
        return <Step4Review vehicleId={vehicleId} vehicle={vehicle} onBack={handleBack} onFinishLater={handleFinishLater} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col-reverse items-start gap-3 xl:flex-row-reverse">
        {currentStep !== 4 && (
          <Panel className="w-full shrink-0 xl:w-[412px]">
            <VehicleDetailsSummary vehicle={vehicle} />
          </Panel>
        )}
        <Panel className={clsx("flex w-full flex-col", currentStep === 4 && "mx-auto max-w-[780px]")}>
          <div className="flex flex-col items-center">
            <StepIndicator currentStep={currentStep} numSteps={TOTAL_STEPS} className="mb-2.5" />
            <Typography variant="title">{stepInfo.title}</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              {stepInfo.description}
            </Typography>
          </div>
          <div className="mt-8">{renderStep()}</div>
        </Panel>
      </div>
      <FinishLaterModal
        open={showFinishLater}
        vehicle={vehicle}
        currentStep={currentStep}
        onKeepGoing={() => setShowFinishLater(false)}
        onFinishLater={handleConfirmFinishLater}
      />
      <VehicleTypesSetupModal open={needsTypeMigration} onClose={() => navigate("../")} valid={typesValid} total={typesTotal} />
    </>
  );
};
